import {
  BadgePoundSterling,
  Check,
  Download,
  Edit,
  FileText,
  LucideIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { applicationStepOne } from "pages/ApplicationForm/formSteps";
import { canContinueApplication } from "pages/ApplicationForm/utils";
import { getCaseDebtConsolidationUrl } from "routes/links";
import useApplicationStore from "store/Application";
import { Case } from "ts/interfaces/common/case";
import { Illustration } from "ts/interfaces/common/illustration";
import { renderLongDate } from "utils/helpers";
import { CaseApplication } from "../types";
import Card from "./Card";

type RowProps = {
  icon: LucideIcon;
  title: string;
  tag?: React.ReactNode;
  description: string;
  action?: React.ReactNode;
};

const Row = ({ icon: Icon, title, tag, description, action }: RowProps) => (
  <li className="flex items-center gap-4 px-6 py-4">
    <span
      aria-hidden="true"
      className="flex size-9 shrink-0 items-center justify-center rounded-md bg-brand-green-light text-brand-green-medium"
    >
      <Icon className="size-4" />
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2 font-medium text-brand-copy-primary">
        {title}
        {tag}
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
    {action}
  </li>
);

const Group = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div>
    <h3 className="bg-brand-gray px-6 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
      {title}
    </h3>
    <ul className="divide-y">{children}</ul>
  </div>
);

interface ApplicationsProps {
  caseDetail: Case;
  caseComplete: boolean;
  canContinue: boolean;
  applications: CaseApplication[];
  illustrations: Illustration[];
}

const Applications: React.FC<ApplicationsProps> = ({
  caseDetail,
  caseComplete,
  canContinue,
  applications,
  illustrations,
}) => {
  const navigate = useNavigate();

  const { resetApplication } = useApplicationStore();

  const setApplicationToContinue = (applicationID: string) => {
    resetApplication();
    navigate(applicationStepOne(applicationID));
  };

  const mostRecentApplication = applications.reduce<
    CaseApplication | undefined
  >(
    (latest, app) =>
      !latest || new Date(app.createdOn) > new Date(latest.createdOn)
        ? app
        : latest,
    undefined,
  );
  const latestSubmission = applications.find((app) => app.isLatestSubmission);

  const describeApplication = (app: CaseApplication) =>
    [
      `Started ${renderLongDate(app.createdOn)}`,
      app.applicationDate && `Submitted ${renderLongDate(app.applicationDate)}`,
    ]
      .filter(Boolean)
      .join(" | ");

  const renderApplicationAction = (app: CaseApplication) => {
    if (caseComplete || app.uuid !== mostRecentApplication?.uuid) {
      return null;
    }

    if (canContinueApplication(app.status)) {
      if (!canContinue) {
        return null;
      }
      return (
        <Button size="sm" onClick={() => setApplicationToContinue(app.uuid)}>
          Continue
        </Button>
      );
    }

    if (app.isLatestSubmission) {
      return (
        <Button asChild size="sm" variant="outline">
          <a href={app.downloadUrl} target="_blank" rel="noreferrer">
            <Download />
            Download
          </a>
        </Button>
      );
    }

    return null;
  };

  const showForms = !!latestSubmission && !caseComplete;
  const isEmpty = !applications.length && !illustrations.length && !showForms;

  return (
    <Card title="Case documents">
      {isEmpty ? (
        <p className="px-6 py-4 text-sm text-muted-foreground">
          No applications or illustrations yet.
        </p>
      ) : (
        <div className="divide-y">
          {illustrations.length > 0 && (
            <Group title="Illustrations">
              {illustrations.map((ill) => (
                <Row
                  key={ill.uuid}
                  icon={BadgePoundSterling}
                  title={`Mortgage illustration ${ill.reference}`}
                  tag={
                    ill.createdBySubmission && (
                      <Badge variant="success" className="gap-1">
                        <Check className="size-3" aria-hidden="true" />
                        Submitted
                      </Badge>
                    )
                  }
                  description={[
                    `Generated ${renderLongDate(ill.createdOn)}`,
                    ill.productReference,
                  ]
                    .filter(Boolean)
                    .join(" | ")}
                  action={
                    <Button asChild size="sm" variant="outline">
                      <a
                        href={ill.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Download />
                        Download
                      </a>
                    </Button>
                  }
                />
              ))}
            </Group>
          )}
          {applications.length > 0 && (
            <Group title="Applications">
              {applications.map((app) => (
                <Row
                  key={app.uuid}
                  icon={FileText}
                  title={`Application ${app.reference}`}
                  description={describeApplication(app)}
                  action={renderApplicationAction(app)}
                />
              ))}
            </Group>
          )}
          {showForms && (
            <Group title="Forms">
              <Row
                icon={FileText}
                title="Debt consolidation form"
                description={`For application ${latestSubmission.reference}`}
                action={
                  <Button asChild size="sm" variant="outline">
                    <Link
                      to={getCaseDebtConsolidationUrl(
                        caseDetail.uuid,
                        latestSubmission.uuid,
                      )}
                    >
                      <Edit />
                      Edit
                    </Link>
                  </Button>
                }
              />
            </Group>
          )}
        </div>
      )}
    </Card>
  );
};

export default Applications;
